import {FC, RefObject, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Center, Flex} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {Loader, useScreenSize} from 'ui-kit';

import {MemoryClusterTabs, MemoryFactItem} from './components';

interface IProps {
  containerRef: RefObject<HTMLDivElement | null>;
}

export const MemoryList: FC<IProps> = () => {
  const {data: settings} = api.settings.useSettingsQuery();
  const {data: clusters = [], isLoading: isClustersLoading} =
    api.memoryFacts.useMemoryClustersQuery();

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  useEffect(() => {
    if (selectedId === undefined && clusters.length > 0) {
      setSelectedId(clusters[0].id);
    }
  }, [clusters, selectedId]);

  const {
    hasNextPage,
    fetchNextPage,
    data: factsData,
    isFetchingNextPage,
    isLoading: isFactsLoading
  } = api.memoryFacts.useMemoryFactsQuery(selectedId);

  const facts = useMemo(() => factsData?.pages.flatMap((page) => page.items) ?? [], [factsData]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isFetchingNextPage) {
        fetchNextPage().then();
      }
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, facts.length]);

  if (isClustersLoading) {
    return (
      <Center h={'100%'}>
        <Loader size={'lg'} />
      </Center>
    );
  }

  if (clusters.length === 0) {
    return (
      <Center flexDirection={'column'} gap={'24px'} h={'100%'} textAlign={'center'} p={'0 24px'}>
        <Box fontWeight={'500'} color={'{colors.textPrimary}'}>
          {t('memory.emptyClusters')}
        </Box>
        <Box color={'{colors.textSecondary}'} maxW={'400px'}>
          {t('memory.emptyClustersDescription', {name: settings?.assistant?.name})}
        </Box>
      </Center>
    );
  }

  return (
    <Flex h={'100%'} gap={'0'} pt={'16px'} align={'stretch'}>
      <Box
        flexShrink={0}
        minH={0}
        w={isLessThan768 ? '140px' : '220px'}
        borderRight={'thin solid'}
        borderColor={'{colors.borderDefault}'}
      >
        <MemoryClusterTabs clusters={clusters} selectedId={selectedId} onSelect={setSelectedId} />
      </Box>

      <Box flex={'1 1 auto'} minH={0} overflowY={'auto'} p={isLessThan768 ? '0 12px' : '0 24px'}>
        {isFactsLoading && (
          <Center h={'100%'}>
            <Loader size={'lg'} />
          </Center>
        )}

        {!isFactsLoading && facts.length === 0 && (
          <Center h={'100%'} color={'{colors.textSecondary}'} textAlign={'center'}>
            {t('memory.emptyFacts')}
          </Center>
        )}

        {!isFactsLoading && facts.length > 0 && (
          <Box>
            {facts.map((fact) => (
              <MemoryFactItem key={fact.id} fact={fact} />
            ))}

            <Box ref={sentinelRef} h={'1px'} />

            {isFetchingNextPage && (
              <Center p={'16px 0'}>
                <Loader size={'sm'} />
              </Center>
            )}
          </Box>
        )}
      </Box>
    </Flex>
  );
};
